import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import portalApi, { getPortalErrorMessage } from '@/lib/portalApi';
import { usePortalAuth } from '@/lib/portalAuth';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import {
  Calendar, FileText, Pill, Receipt, MessageSquare, User, Target,
  ChevronRight, RefreshCw, Clock, AlertCircle
} from 'lucide-react';

function DashboardCard({ title, description, icon: Icon, to, count, badge, loading }) {
  if (loading) {
    return (
      <Card className="h-full">
        <CardContent className="p-5">
          <Skeleton className="h-10 w-10 rounded-lg mb-3" />
          <Skeleton className="h-5 w-24 mb-2" />
          <Skeleton className="h-4 w-32" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Link to={to}>
      <Card className="hover:border-primary/50 transition-colors cursor-pointer h-full group" data-testid={`dashboard-card-${title.toLowerCase().replace(/\s+/g, '-')}`}>
        <CardContent className="p-5">
          <div className="flex items-start justify-between mb-3">
            <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
              <Icon className="h-5 w-5 text-primary" />
            </div>
            {badge && <Badge variant={badge.variant || 'secondary'}>{badge.label}</Badge>}
          </div>
          <div className="flex items-end justify-between">
            <div>
              <p className="font-semibold text-base">{title}</p>
              <p className="text-sm text-muted-foreground">{description}</p>
            </div>
            {count !== undefined && (
              <span className="text-2xl font-bold text-primary">{count}</span>
            )}
          </div>
          <div className="flex items-center gap-1 mt-3 text-xs text-muted-foreground group-hover:text-primary transition-colors">
            View details <ChevronRight className="h-3 w-3" />
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

function ShiftPreview({ shifts, loading }) {
  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
            <Skeleton className="h-10 w-10 rounded-lg" />
            <div className="flex-1">
              <Skeleton className="h-4 w-32 mb-1" />
              <Skeleton className="h-3 w-24" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!shifts || shifts.length === 0) {
    return (
      <div className="text-center py-6 text-muted-foreground">
        <Calendar className="h-8 w-8 mx-auto mb-2 opacity-50" />
        <p className="text-sm">No upcoming supports scheduled</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {shifts.slice(0, 3).map((shift, idx) => (
        <div key={idx} className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors">
          <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Clock className="h-5 w-5 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-sm">{shift.serviceType || 'Support Session'}</p>
            <p className="text-xs text-muted-foreground">
              {new Date(shift.date).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' })}
              {' • '}{shift.startTime} - {shift.endTime}
            </p>
          </div>
          {shift.staffName && (
            <Badge variant="outline" className="text-xs">{shift.staffName}</Badge>
          )}
        </div>
      ))}
      {shifts.length > 3 && (
        <Link to="/portal/shifts">
          <Button variant="ghost" size="sm" className="w-full text-xs">
            View all {shifts.length} upcoming supports
            <ChevronRight className="h-3 w-3 ml-1" />
          </Button>
        </Link>
      )}
    </div>
  );
}

export default function PortalDashboardPage() {
  const { user } = usePortalAuth();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [shifts, setShifts] = useState([]);
  const [messages, setMessages] = useState([]);
  // Phase 6: detect whether the new portal Goals view is available for this
  // session. The flag is server-side only, so we probe the endpoint once.
  const [goalsAvailable, setGoalsAvailable] = useState(false);
  const [goalsCount, setGoalsCount] = useState(0);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [summaryRes, shiftsRes, messagesRes] = await Promise.all([
        portalApi.get('/portal/participant/summary'),
        portalApi.get('/portal/participant/shifts?days_ahead=14'),
        portalApi.get('/portal/messages'),
      ]);
      setSummary(summaryRes.data.data);
      setShifts(shiftsRes.data.data || []);
      setMessages(messagesRes.data.items || []);
    } catch (err) {
      toast.error(getPortalErrorMessage(err));
    } finally {
      setLoading(false);
    }
    // Probe goals endpoint (best-effort; 404 = flag OFF or no access).
    try {
      const g = await portalApi.get('/portal/participant/goals');
      const list = g.data.data?.goals || [];
      setGoalsAvailable(true);
      setGoalsCount(list.length);
    } catch {
      setGoalsAvailable(false);
      setGoalsCount(0);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const openMessages = messages.filter(m => m.status === 'OPEN').length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Welcome, {user?.firstName || 'Guest'}</h1>
          <p className="text-muted-foreground">
            {summary?.participantName ? `Viewing care information for ${summary.participantName}` : 'Loading...'}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchData} disabled={loading} data-testid="refresh-dashboard">
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Summary Card */}
      {summary && (
        <Card className="bg-gradient-to-r from-primary/5 to-primary/10 border-primary/20">
          <CardContent className="p-5">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="h-14 w-14 rounded-xl bg-primary/20 flex items-center justify-center">
                  <User className="h-7 w-7 text-primary" />
                </div>
                <div>
                  <p className="font-semibold text-lg">{summary.participantName}</p>
                  {summary.participantCode && <p className="text-sm text-muted-foreground">ID: {summary.participantCode}</p>}
                </div>
              </div>
              <div className="text-sm sm:text-right text-muted-foreground">
                {summary.phone && <p>{summary.phone}</p>}
                {summary.email && <p>{summary.email}</p>}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Links Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <DashboardCard
          title="Upcoming Supports"
          description="View your scheduled sessions"
          icon={Calendar}
          to="/portal/shifts"
          count={shifts.length}
          loading={loading}
        />
        <DashboardCard
          title="Care Plan"
          description="View your approved care plan"
          icon={FileText}
          to="/portal/care-plan"
          loading={loading}
        />
        {goalsAvailable && (
          <DashboardCard
            title="Goals"
            description="Your approved goals and outcomes"
            icon={Target}
            to="/portal/goals"
            count={goalsCount}
            loading={loading}
          />
        )}
        <DashboardCard
          title="Medications"
          description="View your medication list"
          icon={Pill}
          to="/portal/medications"
          loading={loading}
        />
        <DashboardCard
          title="Messages"
          description="Contact your care team"
          icon={MessageSquare}
          to="/portal/messages"
          count={openMessages > 0 ? openMessages : undefined}
          badge={openMessages > 0 ? { label: 'New', variant: 'destructive' } : undefined}
          loading={loading}
        />
      </div>

      {/* Two-column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Upcoming Supports Preview */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg">Upcoming Supports</CardTitle>
                <CardDescription>Your scheduled sessions for the next 2 weeks</CardDescription>
              </div>
              <Link to="/portal/shifts">
                <Button variant="ghost" size="sm">View all</Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            <ShiftPreview shifts={shifts} loading={loading} />
          </CardContent>
        </Card>

        {/* Support Coordinator */}
        {summary?.supportCoordinator && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Your Support Coordinator</CardTitle>
              <CardDescription>Contact for plan questions</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4 p-4 bg-muted/30 rounded-lg">
                <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="h-6 w-6 text-primary" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold">{summary.supportCoordinator.name}</p>
                  {summary.supportCoordinator.email && (
                    <p className="text-sm text-muted-foreground">{summary.supportCoordinator.email}</p>
                  )}
                  {summary.supportCoordinator.phone && (
                    <p className="text-sm text-muted-foreground">{summary.supportCoordinator.phone}</p>
                  )}
                </div>
                <Link to="/portal/messages">
                  <Button size="sm">
                    <MessageSquare className="h-4 w-4 mr-2" />
                    Message
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quick Actions */}
        <Card className={summary?.supportCoordinator ? 'lg:col-span-2' : ''}>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Link to="/portal/messages">
                <Button variant="outline" className="w-full h-auto py-4 flex-col gap-2">
                  <MessageSquare className="h-5 w-5" />
                  <span className="text-xs">Send Message</span>
                </Button>
              </Link>
              <Link to="/portal/invoices">
                <Button variant="outline" className="w-full h-auto py-4 flex-col gap-2">
                  <Receipt className="h-5 w-5" />
                  <span className="text-xs">View Invoices</span>
                </Button>
              </Link>
              <Link to="/portal/care-plan">
                <Button variant="outline" className="w-full h-auto py-4 flex-col gap-2">
                  <FileText className="h-5 w-5" />
                  <span className="text-xs">Care Plan</span>
                </Button>
              </Link>
              <Link to="/portal/feedback">
                <Button variant="outline" className="w-full h-auto py-4 flex-col gap-2 border-amber-200 hover:border-amber-400">
                  <AlertCircle className="h-5 w-5 text-amber-500" />
                  <span className="text-xs">Give Feedback</span>
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Engagement Widgets */}
        <PortalEngagementWidgets />
      </div>
    </div>
  );
}

function PortalEngagementWidgets() {
  const [announcements, setAnnouncements] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [annRes, aptRes, msgRes] = await Promise.all([
          portalApi.get('/portal/announcements?pageSize=5').catch(() => ({ data: { items: [] } })),
          portalApi.get('/portal/appointments?pageSize=5&status=SCHEDULED').catch(() => ({ data: { items: [] } })),
          portalApi.get('/portal/communication-history?pageSize=5').catch(() => ({ data: { items: [] } })),
        ]);
        setAnnouncements(annRes.data.items || []);
        setAppointments(aptRes.data.items || []);
        setMessages(msgRes.data.items || []);
      } catch { /* ignore */ }
      setLoading(false);
    };
    load();
  }, []);

  if (loading) return null;

  return (
    <>
      {announcements.length > 0 && (
        <Card data-testid="portal-announcements-widget">
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><AlertCircle className="w-4 h-4 text-amber-500" />Recent Announcements</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {announcements.map(a => (
              <div key={a.id} className="border-b border-dashed pb-2 last:border-0">
                <div className="flex items-center gap-2"><Badge variant="outline" className="text-[10px]">{a.announcementType?.replace(/_/g, ' ')}</Badge><span className="text-xs text-muted-foreground">{a.activeFrom?.slice(0, 10)}</span></div>
                <p className="text-sm font-medium mt-0.5">{a.title}</p>
                <p className="text-xs text-muted-foreground line-clamp-2">{a.body}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {appointments.length > 0 && (
        <Card data-testid="portal-appointments-widget">
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Calendar className="w-4 h-4 text-blue-500" />Upcoming Appointments</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {appointments.map(a => (
              <div key={a.id} className="flex items-center justify-between border-b border-dashed pb-2 last:border-0">
                <div>
                  <p className="text-sm font-medium">{a.title}</p>
                  <p className="text-xs text-muted-foreground">{a.scheduledStart?.replace('T', ' ')} {a.location && `· ${a.location}`}</p>
                </div>
                <Badge variant="outline">{a.appointmentType}</Badge>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {messages.length > 0 && (
        <Card data-testid="portal-messages-widget">
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><MessageSquare className="w-4 h-4 text-green-500" />Recent Updates</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {messages.map(m => (
              <div key={m.id} className="border-b border-dashed pb-2 last:border-0">
                <div className="flex items-center gap-2"><Badge variant="outline" className="text-[10px]">{m.messageType?.replace(/_/g, ' ')}</Badge><Badge className={`text-[10px] ${m.status === 'READ' ? 'bg-purple-500' : 'bg-green-500'} text-white`}>{m.status}</Badge></div>
                <p className="text-sm font-medium mt-0.5">{m.subject}</p>
                <p className="text-xs text-muted-foreground">{new Date(m.createdAt).toLocaleDateString()}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </>
  );
}
